import React, { memo, useMemo } from 'react';
import { Gavel, Wallet } from 'lucide-react';
import { useCurrency, useLanguage } from '../../App';
import { getRentalPeriodLabel } from '../../utils/currencyFormatter';

/**
 * ListingCardPrice
 * Handles price display for various listing types:
 * - Sale (fixed price + highest offer if available)
 * - Sale with Bidding (asking price + highest bid)
 * - Rent (monthly price + highest offer if available)
 * - Auction (current bid)
 * - Yard Sale (item count)
 * - Giveaway (free)
 * 
 * Supports dual currency display (USD/XCG)
 */
const ListingCardPrice = memo(({
  listing,
  compact = false,
  showSecondary = true
}) => {
  const { currency, formatDualPrice } = useCurrency();
  const { language } = useLanguage();
  
  // Memoize pricing to avoid recalculating on every render
  const pricing = useMemo(() => 
    listing.pricing || { amount: listing.price || 0, currency: 'USD' },
    [listing.pricing, listing.price]
  );
  
  // Check if this is a bidding-enabled sale listing
  const isBiddingListing = listing.listing_type === 'sale' && pricing.bidding_allowed;
  
  // Calculate primary price based on listing type
  const primaryPrice = useMemo(() => {
    if (listing.listing_type === 'giveaway') return 'Free';
    
    if (listing.listing_type === 'auction') {
      const amount = listing.auction?.current_bid || listing.auction?.starting_price || pricing.amount;
      const prices = formatDualPrice(amount, 'USD');
      return currency === 'XCG' ? prices.xcg : prices.usd;
    }
    
    if (listing.listing_type === 'yard_sale') {
      // Show bundle price if available, otherwise show starting price or "See items"
      if (listing.yard_sale?.bundle_price) {
        const bundlePrices = formatDualPrice(parseFloat(listing.yard_sale.bundle_price), 'USD');
        return currency === 'XCG' ? bundlePrices.xcg : bundlePrices.usd;
      }
      // Fallback to pricing amount or "See items" text
      if (pricing.amount > 0) {
        const prices = formatDualPrice(pricing.amount, pricing.currency);
        return currency === 'XCG' ? prices.xcg : prices.usd;
      }
      return language === 'nl' ? 'Bekijk items' : 'See items';
    }
    
    // For bidding listings, show the asking price
    const prices = formatDualPrice(pricing.amount, pricing.currency);
    return currency === 'XCG' ? prices.xcg : prices.usd;
  }, [listing.listing_type, listing.auction, listing.yard_sale, pricing, currency, formatDualPrice, language]);
  
  // Calculate secondary price/info
  const secondaryPrice = useMemo(() => {
    if (listing.listing_type === 'giveaway') return null;
    
    // Auction bids now shown as badge on image
    if (listing.listing_type === 'auction') {
      return null;
    }
    
    // Yard sale items now shown as badge on image
    if (listing.listing_type === 'yard_sale') {
      return null;
    }
    
    if (listing.listing_type === 'rent') {
      // Get rental period from pricing, default to monthly for backwards compatibility
      const rentalPeriod = listing.pricing?.rental_period || (listing.pricing?.is_hourly ? 'hourly' : 'monthly');
      return `/ ${getRentalPeriodLabel(rentalPeriod, language)}`;
    }
    
    return null;
  }, [listing, language]);
  
  // Reserve price for auctions
  const reservePrice = useMemo(() => {
    if (listing.listing_type !== 'auction' || !listing.auction?.reserve_price) return null;
    
    const reserve = listing.auction.reserve_price;
    return currency === 'XCG' 
      ? `XCG ${(reserve * 1.79)?.toLocaleString('nl-NL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
      : `$ ${reserve?.toLocaleString('nl-NL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  }, [listing, currency]);
  
  // Price unit suffix (e.g., "/m²", "/hectare") - for business listings
  const priceUnitSuffix = useMemo(() => {
    if (!pricing.price_unit) return null;
    // Format as "/m²" or "/stuk" etc.
    return `/${pricing.price_unit}`;
  }, [pricing.price_unit]);
  
  // Highest bid info for bidding listings - only show when there are actual bids
  const highestBidInfo = useMemo(() => {
    if (!isBiddingListing) return null;
    
    const bidCount = listing.bid_count || 0;
    const highestBid = listing.highest_bid || 0;
    
    // Only show bid info when there are actual bids
    if (bidCount === 0) {
      return null;
    }
    
    // If we have bid count but no highest_bid value, just show the count
    if (!highestBid) {
      return {
        label: 'Bids placed',
        amount: null,
        count: bidCount
      };
    }
    
    const bidPrices = formatDualPrice(highestBid, pricing.currency || 'USD');
    return {
      label: 'Highest bid',
      amount: currency === 'XCG' ? bidPrices.xcg : bidPrices.usd,
      count: bidCount
    };
  }, [isBiddingListing, listing.bid_count, listing.highest_bid, currency, formatDualPrice, pricing.currency]);
  
  // Highest offer info - REMOVED: Offers functionality has been removed from the marketplace
  // Only bidding/auction mechanisms are now supported
  const highestOfferInfo = null;
  
  // Determine if this is an auction listing
  const isAuction = listing.listing_type === 'auction';
  
  // Check if price was reduced - show original price crossed out
  const priceReductionInfo = useMemo(() => {
    // Don't show for auctions, yard sales, or giveaways
    if (listing.listing_type === 'auction' || listing.listing_type === 'yard_sale' || listing.listing_type === 'giveaway') {
      return null;
    }
    
    if (!pricing.original_price || pricing.original_price <= pricing.amount) {
      return null;
    }
    
    const originalPrices = formatDualPrice(pricing.original_price, pricing.currency);
    return {
      originalPrice: currency === 'XCG' ? originalPrices.xcg : originalPrices.usd,
      isReduced: true
    };
  }, [listing.listing_type, pricing, currency, formatDualPrice]);
  
  return (
    <div 
      className={compact ? 'mb-1.5' : 'mb-3'}
      data-testid={`listing-price-${listing.id}`}
    >
      <div className="flex flex-col">
        {/* Primary price with optional compact bidding indicator */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Price with price reduction support */}
          {priceReductionInfo ? (
            <div className="flex flex-col" data-testid={`listing-price-reduced-${listing.id}`}>
              <span className="text-[10px] text-muted-foreground line-through leading-tight">
                {priceReductionInfo.originalPrice}
              </span>
              <span className={`${compact ? 'text-sm sm:text-base' : 'text-base sm:text-lg'} font-bold text-green-600 dark:text-green-500 leading-tight flex items-center gap-1.5`}>
                {primaryPrice}
                {/* Price unit suffix (e.g., /m²) */}
                {priceUnitSuffix && (
                  <span className="text-xs font-normal text-muted-foreground">{priceUnitSuffix}</span>
                )}
                {/* Rental period inline with reduced price */}
                {listing.listing_type === 'rent' && secondaryPrice && (
                  <span className="text-xs font-normal text-muted-foreground ml-1">{secondaryPrice}</span>
                )}
              </span>
            </div>
          ) : (
            <span 
              className={`${compact ? 'text-sm sm:text-base' : 'text-base sm:text-lg'} font-bold truncate flex items-center gap-1.5 ${
                isAuction ? 'text-amber-500' : 'text-primary'
              }`}
              data-testid={`listing-primary-price-${listing.id}`}
            >
              {primaryPrice}
              {/* Price unit suffix (e.g., /m²) */}
              {priceUnitSuffix && (
                <span className="text-xs font-normal text-muted-foreground">{priceUnitSuffix}</span>
              )}
              {/* Small gavel icon for auctions */}
              {isAuction && <Gavel className="w-3.5 h-3.5 flex-shrink-0" />}
              {/* Rental period always inline with price */}
              {listing.listing_type === 'rent' && secondaryPrice && (
                <span className="text-xs font-normal text-muted-foreground ml-1">{secondaryPrice}</span>
              )}
            </span>
          )}
          
          {/* USDT indicator - subtle teal badge next to price */}
          {listing.accepts_usdt && listing.status !== 'sold' && listing.status !== 'reserved' && (
            <span 
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-teal-500/15 text-teal-500 text-[10px] font-medium rounded-full"
              title="Accepts USDT payment"
              data-testid={`usdt-price-badge-${listing.id}`}
            >
              <Wallet className="w-3 h-3" />
              USDT
            </span>
          )}
          
          {/* Bidding indicator - shows gavel icon with bid count if there are bids */}
          {isBiddingListing && (
            <div className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded-full">
              <Gavel className="w-3 h-3 text-amber-500" />
              {highestBidInfo && (
                <span className="text-[10px] font-medium text-amber-500">
                  {highestBidInfo.count} bid{highestBidInfo.count !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Secondary price/info - for yard sales and non-bidding listings (rental handled inline above) */}
        {showSecondary && !compact && secondaryPrice && !isBiddingListing && listing.listing_type !== 'rent' && (
          <span 
            className="text-xs text-muted-foreground truncate"
            data-testid={`listing-secondary-price-${listing.id}`}
          >
            {secondaryPrice}
          </span>
        )}
        
        {/* Reserve price removed for compact design */}
      </div>
    </div>
  );
});

ListingCardPrice.displayName = 'ListingCardPrice';

export default ListingCardPrice;
